import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { guardarMaterialAviso, obtenerMaterialesAviso, obtenerProductos } from '../servicios/api';

const MaterialesAviso = ({ avisoId }) => {
  const [materiales, setMateriales] = useState([]);
  const [productos, setProductos] = useState([]);
  const [productoId, setProductoId] = useState('');
  const [concepto, setConcepto] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [precioCoste, setPrecioCoste] = useState('');
  const [totalVenta, setTotalVenta] = useState(0);
  const [totalCoste, setTotalCoste] = useState(0);
  const [costesPendientes, setCostesPendientes] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    obtenerProductos()
      .then((respuesta) => {
        if (!respuesta.exito || !Array.isArray(respuesta.productos)) {
          throw new Error(respuesta.mensaje || 'No se pudo cargar el inventario.');
        }
        setProductos(respuesta.productos);
      })
      .catch((loadError) => {
        setError(loadError.response?.data?.mensaje || loadError.message || 'No se pudo cargar el inventario.');
      });
  }, []);

  const cargarMateriales = useCallback(async () => {
    if (!avisoId) return;
    try {
      const respuesta = await obtenerMaterialesAviso(avisoId);
      if (!respuesta.exito) throw new Error(respuesta.mensaje || 'No se pudieron cargar los repuestos.');
      setMateriales(respuesta.materiales || []);
      setTotalVenta(Number(respuesta.venta_piezas || 0));
      setTotalCoste(Number(respuesta.gasto_piezas || 0));
      setCostesPendientes(Number(respuesta.repuestos_sin_coste || 0));
    } catch (loadError) {
      setError(loadError.response?.data?.mensaje || loadError.message || 'No se pudieron cargar los repuestos.');
    }
  }, [avisoId]);

  useEffect(() => {
    cargarMateriales();
  }, [cargarMateriales]);

  const handleProductoChange = (event) => {
    const id = event.target.value;
    setProductoId(id);
    const producto = productos.find((item) => String(item.id_producto) === id);
    if (!producto) {
      setConcepto('');
      setPrecioCoste('');
      return;
    }
    setConcepto(`${producto.nombre} ${producto.modelo}`.trim());
    setPrecioCoste(producto.precio_compra ?? '');
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    setCargando(true);
    setError('');
    try {
      if (!productoId) {
        throw new Error('Selecciona un repuesto del inventario; el precio de venta se toma automáticamente de su ficha.');
      }
      const productoSeleccionado = productos.find((producto) => String(producto.id_producto) === productoId);
      if (!productoSeleccionado || productoSeleccionado.precio === null || productoSeleccionado.precio === undefined) {
        throw new Error('El producto seleccionado no tiene un precio de venta válido en el inventario.');
      }
      const respuesta = await guardarMaterialAviso({
        accion: 'agregar',
        aviso_id: avisoId,
        tipo: 'repuesto',
        producto_id: productoId || null,
        concepto: concepto.trim(),
        cantidad: Number(cantidad),
        precio_coste: precioCoste === '' ? null : Number(precioCoste),
      });
      if (!respuesta.exito) throw new Error(respuesta.mensaje || 'No se pudo guardar el repuesto.');
      setProductoId('');
      setConcepto('');
      setCantidad('1');
      setPrecioCoste('');
      await cargarMateriales();
    } catch (saveError) {
      setError(saveError.response?.data?.mensaje || saveError.message || 'No se pudo guardar el repuesto.');
    } finally {
      setCargando(false);
    }
  };

  const handleDelete = async (id) => {
    setCargando(true);
    setError('');
    try {
      const respuesta = await guardarMaterialAviso({ accion: 'eliminar', aviso_id: avisoId, id });
      if (!respuesta.exito) throw new Error(respuesta.mensaje || 'No se pudo eliminar el repuesto.');
      await cargarMateriales();
    } catch (deleteError) {
      setError(deleteError.response?.data?.mensaje || deleteError.message || 'No se pudo eliminar el repuesto.');
    } finally {
      setCargando(false);
    }
  };

  return (
    <section className="space-y-3 rounded border border-slate-700 bg-slate-900/60 p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-slate-100">Repuestos</h3>
          <p className="text-xs text-slate-400">La mano de obra se registra en el precio cobrado del aviso.</p>
        </div>
        <div className="text-right text-xs">
          <p className="text-green-300">Venta: {totalVenta.toFixed(2)} €</p>
          <p className="text-amber-300">Coste: {totalCoste.toFixed(2)} €</p>
          <p className="font-semibold text-white">Margen piezas: {(totalVenta - totalCoste).toFixed(2)} €</p>
        </div>
      </div>
      {costesPendientes > 0 && (
        <p className="text-xs text-amber-300">
          Hay {costesPendientes} repuesto(s) sin coste registrado; el margen no estará completo hasta corregirlos.
        </p>
      )}
      {materiales.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-400">
              <tr>
                <th className="py-2">Repuesto</th>
                <th className="py-2">Cant.</th>
                <th className="py-2">Coste/u.</th>
                <th className="py-2">Venta total</th>
                <th className="py-2"><span className="sr-only">Acción</span></th>
              </tr>
            </thead>
            <tbody>
              {materiales.map((material) => {
                const ventaUnitario = Number(material.precio_venta);
                return (
                  <tr key={material.id} className="border-t border-slate-800 text-slate-200">
                    <td className="py-2 pr-2">{material.concepto}</td>
                    <td className="py-2">{material.cantidad}</td>
                    <td className="py-2">{material.precio_coste === null ? 'Pendiente' : `${Number(material.precio_coste).toFixed(2)} €`}</td>
                    <td className="py-2">{(ventaUnitario * Number(material.cantidad)).toFixed(2)} €</td>
                    <td className="py-2 text-right">
                      <button type="button" disabled={cargando} onClick={() => handleDelete(material.id)} aria-label={`Eliminar ${material.concepto}`} className="rounded p-1 text-red-300 hover:bg-red-950 disabled:opacity-50">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-slate-400">Todavía no hay repuestos registrados.</p>}
      <form onSubmit={handleAdd} className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <select value={productoId} onChange={handleProductoChange} required aria-label="Producto del inventario" className="rounded border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white">
          <option value="">Selecciona un repuesto del inventario</option>
          {productos.map((producto) => (
            <option key={producto.id_producto} value={producto.id_producto}>{producto.nombre} · {producto.modelo} · Venta {Number(producto.precio || 0).toFixed(2)} €</option>
          ))}
        </select>
        <input value={concepto} onChange={(event) => setConcepto(event.target.value)} required maxLength="255" placeholder="Nombre del repuesto" aria-label="Concepto" className="rounded border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white" />
        <input value={cantidad} onChange={(event) => setCantidad(event.target.value)} required type="number" min="1" step="1" aria-label="Cantidad" className="rounded border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white" />
        <input value={precioCoste} type="number" min="0" step="0.01" placeholder="Coste tomado del producto" aria-label="Coste de compra por unidad" readOnly className="rounded border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white read-only:opacity-70" />
        <p className="self-center text-xs text-slate-400">La venta se toma automáticamente del precio del producto en Inventario.</p>
        <button type="submit" disabled={cargando} aria-label="Añadir repuesto" className="inline-flex items-center justify-center gap-1 rounded bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50">
          <Plus size={16} /><span>Añadir repuesto</span>
        </button>
      </form>
      {error && <p role="alert" className="text-sm text-red-300">{error}</p>}
    </section>
  );
};

export default MaterialesAviso;
